import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { PlusIcon, XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RowCard } from "@/components/list/row-card";
import { RowIndex } from "@/components/list/row-index";
import { ListEmpty } from "@/components/list/status-text";
import { toneClass, type VersionStateDef } from "@/constants/version-state";
import { StateDialog } from "@/components/version-states/state-dialog";
import { useVersionStates } from "@/hooks/version-states";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { delFetcher, mutateRequest } from "@/lib/swr";
import { cn } from "@/lib/utils";

// Admin-managed version-state catalog (version_state table): adding a flow
// step is a row here, not a code change. Code value doubles as the flow
// order — leave gaps (e.g. 175 between 170 and 180) when inserting.
const VersionStates = () => {
  const { toast } = useToast();
  const rows = useVersionStates() ?? [];
  const [editing, setEditing] = useState<VersionStateDef | null>(null);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<VersionStateDef | null>(null);

  const { trigger: remove, isMutating: deleting } = useSWRMutation(
    "/version-states",
    delFetcher,
    {
      onSuccess: () => {
        mutateRequest("/version-states");
        setRemoving(null);
      },
      onError: (err: unknown) => toastError(toast, "Delete failed", err),
    }
  );

  const labelOf = (code: number) =>
    rows.find((r) => r.code === code)?.label ?? String(code);

  return (
    <div className="flex max-w-3xl flex-col gap-3">
      <div className="flex justify-end">
        <Button size="sm" className="h-8 shrink-0" onClick={() => setAdding(true)}>
          <PlusIcon className="mr-1 h-3.5 w-3.5" />
          Add state
        </Button>
      </div>

      {rows.length === 0 && <ListEmpty>No states.</ListEmpty>}
      {rows.map((r, i) => (
        <RowCard key={r.code}>
          <RowIndex n={i + 1} />
          <span className="w-10 shrink-0 text-right font-mono text-xs tabular-nums text-muted-foreground">
            {r.code}
          </span>
          <span
            className={cn(
              "w-20 shrink-0 rounded px-1.5 py-0.5 text-center text-xs font-medium",
              toneClass(r.color)
            )}
          >
            {r.label}
          </span>
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
            {r.nexts.length
              ? `→ ${r.nexts.map(labelOf).join("、")}`
              : "→ (terminal)"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 px-2 text-xs"
            onClick={() => setEditing(r)}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title="Delete this state"
            className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
            disabled={r.code === 0}
            onClick={() => setRemoving(r)}
          >
            <XIcon className="h-3.5 w-3.5" />
          </Button>
        </RowCard>
      ))}

      {(adding || editing) && (
        <StateDialog
          row={editing ?? undefined}
          rows={rows}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete state {removing?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting is refused while any version sits in this state or any
              app&apos;s flow references it. Other states&apos; default nexts
              drop it automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={() => removing && remove(String(removing.code))}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VersionStates;
